import React, { useState } from 'react';
import {
  Box,
  Container,
  Typography,
  Button,
  Grid,
  Card,
  CardContent,
  CardActions,
  useTheme,
  Chip,
  Switch,
  FormControlLabel,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
} from '@mui/material';
import {
  CheckCircle,
  Cancel,
  ExpandMore,
  Star,
  Bolt,
  Business,
  ArrowForward,
} from '@mui/icons-material';
import { Link } from 'react-router-dom';
import pricingComparisonImage from './pricing-comparison-infographic.jpg';

const PricingPage: React.FC = () => {
  const theme = useTheme();
  const [billingPeriod, setBillingPeriod] = useState<'monthly' | 'yearly'>('monthly');

  const plans = [
    {
      name: 'Free',
      icon: <Star />,
      description: 'Perfect for trying out the platform and setting up new teams',
      price: {
        monthly: 0,
        yearly: 0,
      },
      features: [
        '1 concurrent user',
        '2 teams',
        '1GB storage',
        '100MB max file size',
        'All features included',
        'Upgrade anytime',
      ],
      limitations: [],
      cta: 'Start Free Forever',
      highlighted: false,
    },
    {
      name: 'Small',
      icon: <Bolt />,
      description: 'Great for small teams getting started',
      price: {
        monthly: 249,
        yearly: 224,
      },
      features: [
        '10 concurrent users',
        '10 teams',
        '200GB storage',
        '10GB max file size',
        'All features included',
      ],
      limitations: [],
      cta: 'Get Started',
      highlighted: true,
      badge: 'Most Popular',
    },
    {
      name: 'Medium',
      icon: <Business />,
      description: 'Perfect for growing teams',
      price: {
        monthly: 599,
        yearly: 539,
      },
      features: [
        '25 concurrent users',
        '10 teams',
        '500GB storage',
        '10GB max file size',
        'All features included',
      ],
      limitations: [],
      cta: 'Get Started',
      highlighted: false,
    },
    {
      name: 'Large',
      icon: <Business />,
      description: 'For large organizations with advanced needs',
      price: {
        monthly: 1199,
        yearly: 1079,
      },
      features: [
        '50 concurrent users',
        '10 teams',
        '1TB storage',
        '10GB max file size',
        'All features included',
      ],
      limitations: [],
      cta: 'Get Started',
      highlighted: false,
    },
  ];

  const comparisonFeatures = [
    { feature: 'Concurrent Users', free: '1', small: '10', medium: '25', large: '50' },
    { feature: 'Teams', free: '2', small: '10', medium: '10', large: '10' },
    { feature: 'Storage', free: '1GB', small: '200GB', medium: '500GB', large: '1TB' },
    { feature: 'Max File Size', free: '100MB', small: '10GB', medium: '10GB', large: '10GB' },
    { feature: 'All Features', free: true, small: true, medium: true, large: true },
  ];

  const faqs = [
    {
      question: 'What is a concurrent user?',
      answer: 'A concurrent user is someone actively using the platform at the same time. The limit refers to how many users can be logged in and active simultaneously. For example, with 10 concurrent seats, you can have 100+ team members—10 can use the platform at once while others log in when seats become available.',
    },
    {
      question: 'How does concurrent pricing save me money?',
      answer: 'Traditional platforms charge per user—$20/month x 100 users = $2,000/month. With concurrent pricing, if only 10 users are active simultaneously, you pay for 10 concurrent seats instead of 100 individual seats. This typically reduces costs by 80-90% for teams with mixed usage patterns.',
    },
    {
      question: 'Can I add unlimited team members?',
      answer: 'Yes! Add as many team members as you want at no additional cost. You only pay for the number of concurrent users who can be active simultaneously. This makes it perfect for teams with a mix of heavy users (daily) and light users (weekly or monthly).',
    },
    {
      question: 'Is the Free plan really free forever?',
      answer: 'Yes! The Free plan is completely free forever with 1 concurrent user, 2 teams, 1GB storage, and all core features. It\'s perfect for trying out the platform. Upgrade to a paid plan anytime you need more concurrent users, teams, storage, or analytics.',
    },
    {
      question: 'What happens if all concurrent seats are in use?',
      answer: 'If all seats are in use, additional users will need to wait until a seat becomes available (when someone logs out or becomes inactive). In practice, most teams find that 10 concurrent seats easily serve 100+ users because not everyone uses the system simultaneously.',
    },
    {
      question: 'Can I share subscriptions across multiple teams?',
      answer: 'Yes! Organizations can share a subscription pool across multiple teams. This dramatically improves efficiency—buy one Large subscription instead of multiple Small ones and share the concurrent user pool and storage across all teams.',
    },
    {
      question: 'Can I add more concurrent users or storage to my plan?',
      answer: 'Absolutely! Add-on packs let you scale independently. Concurrent user packs: 5 users ($99), 10 users ($179), 25 users ($399). Storage packs: 100GB ($49), 500GB ($199), 1TB ($349). Combine any add-ons with any base plan.',
    },
    {
      question: 'Is there a setup fee or long-term contract?',
      answer: 'No setup fees and no long-term contracts. All plans (including Free) are month-to-month with optional annual billing on paid plans (10% discount). Cancel anytime with no penalties.',
    },
  ];

  return (
    <>
      {/* Hero Section */}
      <Box
        sx={{
          background: `linear-gradient(135deg, ${theme.palette.primary.main}15 0%, ${theme.palette.secondary.main}15 100%)`,
          pt: { xs: 6, md: 10 },
          pb: { xs: 6, md: 10 },
        }}
      >
        <Container maxWidth="lg">
          <Box sx={{ textAlign: 'center' }}>
            <Typography
              variant="h1"
              sx={{
                fontSize: { xs: '2.5rem', md: '3.5rem' },
                fontWeight: 800,
                mb: 3,
                background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.secondary.main} 100%)`,
                backgroundClip: 'text',
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent',
              }}
            >
              Revolutionary Concurrent User Pricing
            </Typography>
            <Typography variant="h5" color="text.secondary" sx={{ mb: 4 }}>
              Pay only for simultaneous active users. Add unlimited team members for free. Start free forever, upgrade when ready.
            </Typography>
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', mb: 6 }}>
              <FormControlLabel
                control={
                  <Switch
                    checked={billingPeriod === 'yearly'}
                    onChange={(e) => setBillingPeriod(e.target.checked ? 'yearly' : 'monthly')}
                    color="primary"
                  />
                }
                label={
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Typography>Annual billing</Typography>
                    <Chip label="Save 10%" color="success" size="small" />
                  </Box>
                }
              />
            </Box>
          </Box>
        </Container>
      </Box>

      {/* Pricing Cards */}
      <Container maxWidth="lg" sx={{ mt: -6, position: 'relative', zIndex: 1, pt: 6 }}>
        <Grid container spacing={3} alignItems="stretch">
          {plans.map((plan) => (
            <Grid item xs={12} sm={6} md={3} key={plan.name}>
              <Card
                sx={{
                  height: '100%',
                  position: 'relative',
                  border: plan.highlighted ? `2px solid ${theme.palette.primary.main}` : '1px solid',
                  borderColor: plan.highlighted ? 'primary.main' : 'divider',
                  transform: plan.highlighted ? 'scale(1.05)' : 'none',
                  boxShadow: plan.highlighted ? 8 : 1,
                  display: 'flex',
                  flexDirection: 'column',
                }}
              >
                {plan.badge && (
                  <Chip
                    label={plan.badge}
                    color="primary"
                    size="small"
                    sx={{
                      position: 'absolute',
                      top: -2,
                      left: '50%',
                      transform: 'translateX(-50%)',
                      zIndex: 1,
                    }}
                  />
                )}
                <CardContent sx={{ p: 4, flexGrow: 1, pb: 2 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                    {plan.icon}
                    <Typography variant="h4" sx={{ ml: 1, fontWeight: 600 }}>
                      {plan.name}
                    </Typography>
                  </Box>
                  <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
                    {plan.description}
                  </Typography>
                  <Box sx={{ mb: 4 }}>
                    {typeof plan.price[billingPeriod] === 'number' ? (
                      <>
                        <Typography variant="h2" sx={{ fontWeight: 700 }}>
                          ${billingPeriod === 'yearly'
                            ? (plan.price.yearly * 12).toLocaleString()
                            : plan.price.monthly.toLocaleString()}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          per {billingPeriod === 'monthly' ? 'month' : 'year'}
                        </Typography>
                      </>
                    ) : (
                      <Typography variant="h2" sx={{ fontWeight: 700 }}>
                        {plan.price[billingPeriod]}
                      </Typography>
                    )}
                  </Box>
                  <List dense>
                    {plan.features.map((feature) => (
                      <ListItem key={feature} sx={{ px: 0 }}>
                        <ListItemIcon sx={{ minWidth: 32 }}>
                          <CheckCircle color="success" fontSize="small" />
                        </ListItemIcon>
                        <ListItemText primary={feature} />
                      </ListItem>
                    ))}
                    {plan.limitations.map((limitation) => (
                      <ListItem key={limitation} sx={{ px: 0 }}>
                        <ListItemIcon sx={{ minWidth: 32 }}>
                          <Cancel color="disabled" fontSize="small" />
                        </ListItemIcon>
                        <ListItemText
                          primary={limitation}
                          primaryTypographyProps={{ color: 'text.secondary' }}
                        />
                      </ListItem>
                    ))}
                  </List>
                </CardContent>
                <CardActions sx={{ p: 4, pt: 0, mt: 'auto' }}>
                  <Button
                    fullWidth
                    variant={plan.highlighted ? 'contained' : 'outlined'}
                    size="large"
                    component={Link}
                    to='/signup'
                    endIcon={<ArrowForward />}
                  >
                    {plan.cta}
                  </Button>
                </CardActions>
              </Card>
            </Grid>
          ))}
        </Grid>
      </Container>

      {/* Cost Comparison Visual */}
      <Container maxWidth="lg" sx={{ py: 10 }}>
        <Box sx={{ textAlign: 'center', mb: 6 }}>
          <Typography variant="h3" sx={{ mb: 2, fontWeight: 700 }}>
            See The Dramatic Cost Difference
          </Typography>
          <Typography variant="h6" color="text.secondary">
            Traditional per-seat pricing vs. concurrent user pricing for 100 team members
          </Typography>
        </Box>
        <Box
          sx={{
            borderRadius: 3,
            overflow: 'hidden',
            boxShadow: 3,
            maxWidth: '100%',
            mx: 'auto',
          }}
        >
          <img
            src={pricingComparisonImage}
            alt="Dramatic cost savings comparison: Traditional per-seat model ($2,000/mo for 100 users) vs. Tier One concurrent model ($249/mo for 10 concurrent seats serving 100 users) showing 80-90% savings"
            style={{
              width: '100%',
              height: 'auto',
              display: 'block',
            }}
          />
        </Box>
      </Container>

      {/* Calculator callout */}
      <Box
        sx={{
          background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.secondary.main} 100%)`,
          py: 6,
        }}
      >
        <Container maxWidth="lg">
          <Grid container spacing={4} alignItems="center">
            <Grid item xs={12} md={8}>
              <Typography variant="h3" sx={{ color: '#fff', fontWeight: 800, mb: 1 }}>
                How does Tier One Content stack up against what you're paying now?
              </Typography>
              <Typography variant="h6" sx={{ color: 'rgba(255,255,255,0.85)', fontWeight: 400 }}>
                Enter your team size and see a side-by-side cost breakdown vs. Highspot, Seismic, Showpad,
                and more — including setup fees and 5-year total cost.
              </Typography>
            </Grid>
            <Grid item xs={12} md={4} sx={{ textAlign: { xs: 'left', md: 'right' } }}>
              <Button
                component={Link}
                to="/compare"
                variant="contained"
                size="large"
                endIcon={<ArrowForward />}
                sx={{
                  fontWeight: 700,
                  fontSize: '1rem',
                  py: 1.75,
                  px: 4,
                  background: '#ffffff',
                  color: theme.palette.primary.dark,
                  '&:hover': { background: 'rgba(255,255,255,0.92)' },
                }}
              >
                Open the calculator
              </Button>
              <Typography variant="caption" sx={{ display: 'block', mt: 1.5, color: 'rgba(255,255,255,0.65)' }}>
                Free to use • No sign-up required
              </Typography>
            </Grid>
          </Grid>
        </Container>
      </Box>

      {/* Add-on Packs Section */}
      <Container maxWidth="lg" sx={{ py: 10 }}>
        <Box sx={{ textAlign: 'center', mb: 6 }}>
          <Typography variant="h3" sx={{ mb: 2, fontWeight: 700 }}>
            Scale Your Plan with Add-ons
          </Typography>
          <Typography variant="h6" color="text.secondary">
            Expand your capacity independently—no forced tier upgrades
          </Typography>
        </Box>
        <Grid container spacing={4}>
          {/* Concurrent Users Add-ons */}
          <Grid item xs={12} md={6}>
            <Card sx={{ height: '100%' }}>
              <CardContent sx={{ p: 4 }}>
                <Typography variant="h5" sx={{ mb: 3, fontWeight: 600 }}>
                  Concurrent User Packs
                </Typography>
                <List>
                  <ListItem sx={{ px: 0, py: 2, borderBottom: `1px solid ${theme.palette.divider}` }}>
                    <ListItemText
                      primary="5 Concurrent Users"
                      secondary="Add 5 concurrent user seats to your plan"
                    />
                    <Typography variant="h6" sx={{ fontWeight: 600 }}>
                      ${billingPeriod === 'monthly' ? '99' : '1,068'}
                      <Typography component="span" variant="body2" color="text.secondary">
                        /{billingPeriod === 'monthly' ? 'mo' : 'yr'}
                      </Typography>
                    </Typography>
                  </ListItem>
                  <ListItem sx={{ px: 0, py: 2, borderBottom: `1px solid ${theme.palette.divider}` }}>
                    <ListItemText
                      primary="10 Concurrent Users"
                      secondary="Add 10 concurrent user seats (volume discount)"
                    />
                    <Typography variant="h6" sx={{ fontWeight: 600 }}>
                      ${billingPeriod === 'monthly' ? '179' : '1,932'}
                      <Typography component="span" variant="body2" color="text.secondary">
                        /{billingPeriod === 'monthly' ? 'mo' : 'yr'}
                      </Typography>
                    </Typography>
                  </ListItem>
                  <ListItem sx={{ px: 0, py: 2 }}>
                    <ListItemText
                      primary="25 Concurrent Users"
                      secondary="Add 25 concurrent user seats (best value)"
                    />
                    <Typography variant="h6" sx={{ fontWeight: 600 }}>
                      ${billingPeriod === 'monthly' ? '399' : '4,308'}
                      <Typography component="span" variant="body2" color="text.secondary">
                        /{billingPeriod === 'monthly' ? 'mo' : 'yr'}
                      </Typography>
                    </Typography>
                  </ListItem>
                </List>
              </CardContent>
            </Card>
          </Grid>

          {/* Storage Add-ons */}
          <Grid item xs={12} md={6}>
            <Card sx={{ height: '100%' }}>
              <CardContent sx={{ p: 4 }}>
                <Typography variant="h5" sx={{ mb: 3, fontWeight: 600 }}>
                  Storage Packs
                </Typography>
                <List>
                  <ListItem sx={{ px: 0, py: 2, borderBottom: `1px solid ${theme.palette.divider}` }}>
                    <ListItemText
                      primary="100GB Storage"
                      secondary="Add 100GB of storage to your plan"
                    />
                    <Typography variant="h6" sx={{ fontWeight: 600 }}>
                      ${billingPeriod === 'monthly' ? '49' : '528'}
                      <Typography component="span" variant="body2" color="text.secondary">
                        /{billingPeriod === 'monthly' ? 'mo' : 'yr'}
                      </Typography>
                    </Typography>
                  </ListItem>
                  <ListItem sx={{ px: 0, py: 2, borderBottom: `1px solid ${theme.palette.divider}` }}>
                    <ListItemText
                      primary="500GB Storage"
                      secondary="Add 500GB of storage (volume discount)"
                    />
                    <Typography variant="h6" sx={{ fontWeight: 600 }}>
                      ${billingPeriod === 'monthly' ? '199' : '2,148'}
                      <Typography component="span" variant="body2" color="text.secondary">
                        /{billingPeriod === 'monthly' ? 'mo' : 'yr'}
                      </Typography>
                    </Typography>
                  </ListItem>
                  <ListItem sx={{ px: 0, py: 2 }}>
                    <ListItemText
                      primary="1TB Storage"
                      secondary="Add 1TB of storage (best value)"
                    />
                    <Typography variant="h6" sx={{ fontWeight: 600 }}>
                      ${billingPeriod === 'monthly' ? '349' : '3,768'}
                      <Typography component="span" variant="body2" color="text.secondary">
                        /{billingPeriod === 'monthly' ? 'mo' : 'yr'}
                      </Typography>
                    </Typography>
                  </ListItem>
                </List>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
        <Box sx={{ textAlign: 'center', mt: 4 }}>
          <Typography variant="body1" color="text.secondary">
            All add-ons can be combined with any base plan and adjusted at any time
            {billingPeriod === 'yearly' && ' • Annual add-ons include 10% discount'}
          </Typography>
        </Box>
      </Container>

      {/* Comparison Table */}
      <Container maxWidth="lg" sx={{ py: 10 }}>
        <Box sx={{ textAlign: 'center', mb: 6 }}>
          <Typography variant="h3" sx={{ mb: 2, fontWeight: 700 }}>
            Compare Plans
          </Typography>
          <Typography variant="h6" color="text.secondary">
            Detailed comparison of all features across plans
          </Typography>
        </Box>
        <TableContainer component={Paper} elevation={0} sx={{ border: `1px solid ${theme.palette.divider}` }}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Feature</TableCell>
                <TableCell align="center">Free</TableCell>
                <TableCell align="center">Small</TableCell>
                <TableCell align="center">Medium</TableCell>
                <TableCell align="center">Large</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {comparisonFeatures.map((row) => (
                <TableRow key={row.feature}>
                  <TableCell component="th" scope="row">
                    {row.feature}
                  </TableCell>
                  <TableCell align="center">
                    {typeof row.free === 'boolean' ? (
                      row.free ? (
                        <CheckCircle color="success" />
                      ) : (
                        <Cancel color="disabled" />
                      )
                    ) : (
                      row.free
                    )}
                  </TableCell>
                  <TableCell align="center">
                    {typeof row.small === 'boolean' ? (
                      row.small ? (
                        <CheckCircle color="success" />
                      ) : (
                        <Cancel color="disabled" />
                      )
                    ) : (
                      row.small
                    )}
                  </TableCell>
                  <TableCell align="center">
                    {typeof row.medium === 'boolean' ? (
                      row.medium ? (
                        <CheckCircle color="success" />
                      ) : (
                        <Cancel color="disabled" />
                      )
                    ) : (
                      row.medium
                    )}
                  </TableCell>
                  <TableCell align="center">
                    {typeof row.large === 'boolean' ? (
                      row.large ? (
                        <CheckCircle color="success" />
                      ) : (
                        <Cancel color="disabled" />
                      )
                    ) : (
                      row.large
                    )}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </Container>

      {/* FAQs */}
      <Box sx={{ backgroundColor: 'background.default', py: 10 }}>
        <Container maxWidth="md">
          <Box sx={{ textAlign: 'center', mb: 6 }}>
            <Typography variant="h3" sx={{ mb: 2, fontWeight: 700 }}>
              Frequently Asked Questions
            </Typography>
            <Typography variant="h6" color="text.secondary">
              Everything you need to know about our pricing
            </Typography>
          </Box>
          {faqs.map((faq, index) => (
            <Accordion key={index} elevation={0} sx={{ mb: 2, border: `1px solid ${theme.palette.divider}` }}>
              <AccordionSummary expandIcon={<ExpandMore />}>
                <Typography variant="h6">{faq.question}</Typography>
              </AccordionSummary>
              <AccordionDetails>
                <Typography color="text.secondary">{faq.answer}</Typography>
              </AccordionDetails>
            </Accordion>
          ))}
        </Container>
      </Box>

      {/* CTA Section */}
      <Box
        sx={{
          background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.secondary.main} 100%)`,
          py: 8,
          color: 'white',
        }}
      >
        <Container maxWidth="md" sx={{ textAlign: 'center' }}>
          <Typography variant="h3" sx={{ mb: 3, fontWeight: 700 }}>
            Start Saving 80-90% on Content Sharing
          </Typography>
          <Typography variant="h6" sx={{ mb: 4, opacity: 0.95 }}>
            Join teams using Tier One Content to share more content at dramatically lower costs
          </Typography>
          <Button
            component={Link}
            to="/signup"
            variant="contained"
            size="large"
            sx={{
              py: 1.5,
              px: 4,
              background: '#ffffff',
              color: theme.palette.primary.dark,
              fontWeight: 700,
              '&:hover': {
                background: 'rgba(255, 255, 255, 0.92)',
              },
            }}
          >
            Start Free Forever
          </Button>
        </Container>
      </Box>
    </>
  );
};

export default PricingPage;